import { Check, ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import type { FieldOption } from '../../../data/geneticFieldOptions';

interface MultiSelectDropdownProps {
  readonly options: readonly FieldOption[];
  readonly value: readonly string[];
  readonly onChange: (next: string[]) => void;
  readonly disabled?: boolean;
  readonly ariaLabel?: string;
}

/**
 * Dropdown multi-select — a compact trigger showing the current selection,
 * opening a scrollable checkbox list. Preserves click order (append on select,
 * drop on deselect) so array-valued DTO fields round-trip in the picked order.
 */
export function MultiSelectDropdown({
  options,
  value,
  onChange,
  disabled = false,
  ariaLabel,
}: MultiSelectDropdownProps): ReactElement {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const selected = new Set(value);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
    };
  }, [open]);

  const labelFor = (option: FieldOption): string =>
    option.labelKey ? t(option.labelKey) : (option.label ?? option.value);

  const summary =
    value.length === 0
      ? t('patient.geneticInfo.fields.multiSelectPlaceholder')
      : options
          .filter((o) => selected.has(o.value))
          .map(labelFor)
          .join(', ');

  const toggle = (code: string): void => {
    if (selected.has(code)) onChange(value.filter((v) => v !== code));
    else onChange([...value, code]);
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => {
          setOpen((prev) => !prev);
        }}
        className={cn(
          'flex h-10 w-full items-center gap-2 rounded-md border border-border bg-card px-3 text-sm',
          'text-card-foreground disabled:cursor-not-allowed disabled:opacity-50',
        )}
      >
        <span className={cn('flex-1 truncate text-left', value.length === 0 && 'text-muted-foreground')}>
          {summary}
        </span>
        {value.length > 0 && (
          <span className="shrink-0 rounded bg-muted px-1.5 text-xs text-muted-foreground">
            {value.length}
          </span>
        )}
        <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
      </button>

      {open && (
        <div
          role="listbox"
          aria-label={ariaLabel}
          aria-multiselectable="true"
          className="absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-lg"
        >
          {options.map((option) => {
            const isOn = selected.has(option.value);
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={isOn}
                onClick={() => {
                  toggle(option.value);
                }}
                className={cn(
                  'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm',
                  'hover:bg-accent hover:text-accent-foreground',
                )}
              >
                <span
                  className={cn(
                    'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                    isOn
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border',
                  )}
                >
                  {isOn && <Check className="h-3 w-3" aria-hidden="true" />}
                </span>
                {labelFor(option)}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
